import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const ShowcaseComments = ({ showcaseId }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const fetchComments = async () => {
    try {
      const response = await fetch(`/api/comments/showcase/${showcaseId}`);
      const data = await response.json();
      
      if (data.success) {
        setComments(data.data.comments);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const handleExpand = () => {
    if (!expanded) {
      fetchComments();
    }
    setExpanded(!expanded);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!newComment.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/comments/showcase/${showcaseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content: newComment.trim(),
          parentCommentId: replyTo
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setNewComment('');
        setReplyTo(null);
        fetchComments();
      }
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const response = await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchComments();
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const formatTime = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    if (seconds < 604800) return Math.floor(seconds / 86400) + 'd ago';
    
    return new Date(date).toLocaleDateString();
  };

  const CommentItem = ({ comment, isReply = false }) => (
    <div style={{
      marginLeft: isReply ? '30px' : '0',
      marginTop: '12px',
      padding: '12px',
      backgroundColor: 'var(--card-bg-secondary)',
      borderRadius: '8px',
      borderLeft: isReply ? '3px solid var(--accent)' : 'none'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold', color: 'var(--accent)' }}>
            {comment.userId.username}
          </span>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {formatTime(comment.createdAt)}
            {comment.isEdited && ' (edited)'}
          </span>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isReply && (
            <button
              onClick={() => setReplyTo(comment._id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                padding: '4px 8px'
              }}
            >
              Reply
            </button>
          )}
          {isAuthenticated && user._id === comment.userId._id && (
            <button
              onClick={() => handleDeleteComment(comment._id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ff4757',
                cursor: 'pointer',
                fontSize: '0.85rem',
                padding: '4px 8px'
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
      
      <div style={{ color: 'var(--text)', fontSize: '0.95rem' }}>
        {comment.content}
      </div>

      {comment.replies && comment.replies.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          {comment.replies.map(reply => (
            <CommentItem key={reply._id} comment={reply} isReply={true} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: '15px' }}>
      <button
        onClick={handleExpand}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--accent)',
          cursor: 'pointer',
          fontSize: '0.95rem',
          fontWeight: '500',
          padding: '8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        💬 {expanded ? 'Hide' : 'Show'} Comments {comments.length > 0 && `(${comments.length})`}
      </button>

      {expanded && (
        <div style={{ marginTop: '15px' }}>
          {/* Comment form */}
          <form onSubmit={handleSubmitComment} style={{ marginBottom: '20px' }}>
            {replyTo && (
              <div style={{
                padding: '8px',
                backgroundColor: 'var(--card-bg-secondary)',
                borderRadius: '4px',
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Replying to comment...
                </span>
                <button
                  type="button"
                  onClick={() => setReplyTo(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '1.2rem'
                  }}
                >
                  ✕
                </button>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyTo ? "Write a reply..." : "Write a comment..."}
                style={{
                  flex: 1,
                  padding: '10px 15px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg-secondary)',
                  color: 'var(--text)'
                }}
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !newComment.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: loading || !newComment.trim() ? 0.6 : 1
                }}
              >
                {loading ? '...' : 'Post'}
              </button>
            </div>
          </form>

          {/* Comments list */}
          {comments.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: 'var(--text-secondary)',
              fontSize: '0.9rem'
            }}>
              No comments yet. Be the first to comment!
            </div>
          ) : (
            comments.map(comment => (
              <CommentItem key={comment._id} comment={comment} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ShowcaseComments;
